import { useState } from "react";

import { programKm, programSecs, programTotals, validateProgram } from "../domain/program";
import type { ProgramBlock, TreadmillProgram } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { Button, IconButton } from "./Button";
import { I } from "./icons";
import { Notice } from "./Notice";
import { NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";

/**
 * Add or edit one of the machine's own programs, block by block: minutes, km/h, incline %.
 * `validateProgram` refuses shapes that would give nonsense totals rather than storing them, and
 * delete asks inside the sheet — the viewer has no `confirm()`. Render it only while open.
 */
export function ProgramEditorSheet({
  program,
  isNew,
  onClose,
}: {
  program: TreadmillProgram;
  isNew: boolean;
  onClose: () => void;
}) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const [draft, setDraft] = useState<TreadmillProgram>(program);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const editBlock = (i: number, patch: Partial<ProgramBlock>) =>
    setDraft((d) => ({ ...d, blocks: d.blocks.map((b, n) => (n === i ? { ...b, ...patch } : b)) }));

  const addBlock = () =>
    setDraft((d) => {
      const last = d.blocks[d.blocks.length - 1];
      return { ...d, blocks: [...d.blocks, { ...(last || { mins: 2, speed: 4, incline: 0 }) }] };
    });

  const removeBlock = (i: number) => setDraft((d) => ({ ...d, blocks: d.blocks.filter((_, n) => n !== i) }));

  const save = () => {
    const problem = validateProgram(draft);
    if (problem) {
      setErr(problem);
      return;
    }
    update((d) => {
      const list = d.profile.programs || [];
      const at = list.findIndex((p) => p.id === draft.id);
      if (at >= 0) list[at] = draft;
      else list.push(draft);
      d.profile.programs = list;
    });
    showToast("Program saved");
    onClose();
  };

  const remove = () => {
    update((d) => {
      d.profile.programs = (d.profile.programs || []).filter((p) => p.id !== draft.id);
    });
    showToast(`${program.name || "Program"} deleted`);
    onClose();
  };

  // A half-typed block makes the totals meaningless, so they wait until every number is a number.
  const valid = !validateProgram(draft);
  const summary = valid
    ? (() => {
        const t = programTotals(S, draft, !!S.profile.inclineOverride);
        return `${draft.blocks.length} blocks · ${Math.round(t.secs / 60)} min · ${t.km.toFixed(2)} km · ~${t.kcal} kcal est.`;
      })()
    : `${draft.blocks.length} blocks · ${Math.round(programSecs(draft) / 60) || "–"} min · ${
        Number.isFinite(programKm(draft)) ? programKm(draft).toFixed(2) : "–"
      } km`;

  const name = program.name.trim() || "this";

  return (
    <Sheet
      title={isNew ? "Add a program" : "Edit program"}
      sub={summary}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          primary={{ label: "Save program", onClick: save }}
          secondary={{ label: "Cancel", onClick: onClose }}
        />
      }
    >
      <div className="field">
        <label htmlFor="pe-name">Name</label>
        <input
          id="pe-name"
          value={draft.name}
          placeholder="P7"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </div>

      <p className="train-field-note">One row per block: how long, at what speed and incline.</p>

      {draft.blocks.map((b, i) => (
        <div className="train-block" key={i}>
          <div className="train-fields">
            <div className="field">
              <label htmlFor={`pe-m${i}`}>Block {i + 1} · min</label>
              <input
                id={`pe-m${i}`}
                type="number"
                inputMode="decimal"
                value={b.mins}
                onChange={(e) => editBlock(i, { mins: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label htmlFor={`pe-s${i}`}>km/h</label>
              <input
                id={`pe-s${i}`}
                type="number"
                inputMode="decimal"
                step="0.1"
                value={b.speed}
                onChange={(e) => editBlock(i, { speed: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label htmlFor={`pe-i${i}`}>Incline %</label>
              <input
                id={`pe-i${i}`}
                type="number"
                inputMode="decimal"
                step="0.5"
                value={b.incline}
                onChange={(e) => editBlock(i, { incline: Number(e.target.value) })}
              />
            </div>
          </div>
          <IconButton icon={I.close} label={`Remove block ${i + 1}`} onClick={() => removeBlock(i)} />
        </div>
      ))}

      <div>
        <Button variant="ghost" icon={I.plus} onClick={addBlock}>
          Add block
        </Button>
      </div>

      {err ? (
        <Notice tone="danger" onDismiss={() => setErr(null)}>
          {err}
        </Notice>
      ) : null}

      {isNew ? null : confirming ? (
        <Notice
          tone="danger"
          primary={{ label: `Delete ${name}`, onClick: remove }}
          secondary={{ label: "Keep it", onClick: () => setConfirming(false) }}
        >
          Delete the {name} program? Runs already logged keep their figures.
        </Notice>
      ) : (
        <div className="train-sheet-rows">
          <RowGroup>
            <NavRow kind="danger" title={`Delete the ${name} program`} icon={I.trash} onClick={() => setConfirming(true)} />
          </RowGroup>
        </div>
      )}
    </Sheet>
  );
}
